import type { ReactNode } from 'react';
import type { Column, TableRow } from './DataTable';

/**
 * A DESK TABLE, AS PHONE CARDS — the shape every phone board turns a table
 * into, from the same `columns` and `rows` the desk screen already built.
 *
 * WHY IT TAKES THE TABLE'S OWN PROPS. A phone list assembled separately is a
 * second opinion about the same rows: two places deciding what a patient's
 * state is, which drift the first time one of them is fixed. Handing this the
 * table's `rows` means the card and the row are the same cells, rendered in a
 * different box.
 *
 * HOW A ROW BECOMES A CARD:
 *   - `body` is the card's top line — on most boards the first column is
 *     already a "name over a caption" block, which is what a card's body is,
 *     so it defaults to the first column;
 *   - `trailing` is the chip on the right, defaulting to the last column
 *     because that is where every board puts a state;
 *   - every OTHER column stacks under the body as a muted line, unless it is
 *     named in `hide`.
 *
 * Both are named rather than positional because a desk table is free to lead
 * with something a card should not: the nurse's day leads with a Lane chip,
 * which belongs beside the state on a phone, not as the title.
 *
 * SELECTING AND OPENING ARE ONE ACT. There is no pane to dock into at this
 * width, so a card has one gesture. Pages that genuinely need a second verb
 * put it in the card's own content, not in a row-level affordance nobody can
 * see.
 */
export function PhoneCards({
  columns,
  rows,
  body: bodyKey,
  trailing: trailingKey,
  hide,
  onOpen,
  empty,
  cardStyle,
  labelMiddle,
}: {
  columns: Column[];
  rows: TableRow[];
  /** The card's top line. Defaults to the first column. */
  body?: string;
  /** The chip on the right. Defaults to the last column. */
  trailing?: string;
  /** Columns a card has no room for — a desk-only "where it stands". */
  hide?: string[];
  onOpen?: (row: TableRow) => void;
  /** What to draw when there is nothing — the page's own EmptyState. */
  empty?: ReactNode;
  /** Per-row styling the page owns, e.g. an amber edge on an urgent row. */
  cardStyle?: (row: TableRow) => React.CSSProperties | undefined;
  /** Draw the stacked columns as ONE line of "Label value" pairs, so bare
   *  numbers from a report table are not left unlabelled on a card. */
  labelMiddle?: boolean;
}) {
  if (!rows.length) return <>{empty}</>;

  const body = columns.find((c) => c.key === bodyKey) ?? columns[0];
  const trailing =
    columns.find((c) => c.key === trailingKey) ??
    (columns.length > 1 ? columns[columns.length - 1] : null);
  const skip = new Set([body.key, trailing?.key, ...(hide ?? [])]);
  const middle = columns.filter((c) => !skip.has(c.key));

  return (
    <>
      {rows.map((r) => {
        /* A heading row is a label in the list, not a card — the table uses it
           to say "Standby · 2" over the rows it owns, and a card would make it
           look like one of them. */
        if (r.section) {
          return (
            <p key={r.id} className="t-ovl mut" style={{ margin: '16px 0 6px' }}>
              {r.section}
            </p>
          );
        }

        const inner = (
          <>
            <div className="flex" style={{ gap: 10, alignItems: 'flex-start' }}>
              <div className="grow" style={{ minWidth: 0 }}>
                {r.cells[body.key]}
                {labelMiddle ? (
                  <p className="t-sec mut mt4" style={{ display: 'flex', flexWrap: 'wrap', columnGap: 10 }}>
                    {middle.filter((c) => r.cells[c.key]).map((c) => (
                      <span key={c.key}>{c.label} <span className="sec w6">{r.cells[c.key]}</span></span>
                    ))}
                  </p>
                ) : null}
                {(labelMiddle ? [] : middle).map((c) =>
                  r.cells[c.key] ? (
                    <div key={c.key} className="t-sec mut mt4">
                      {r.cells[c.key]}
                    </div>
                  ) : null,
                )}
              </div>
              {/* `flexShrink: 0` keeps a chip whole; the cap keeps a cell that
                  is NOT a chip from eating the card. The manager's branch row
                  trails with a nowrap list of doctors, which at flex-shrink 0
                  took the whole width and squeezed the branch name into a
                  one-character column. */}
              {trailing && r.cells[trailing.key] ? (
                <span style={{ flexShrink: 0, maxWidth: '55%', minWidth: 0 }}>
                  {r.cells[trailing.key]}
                </span>
              ) : null}
            </div>
          </>
        );

        const style = {
          ...(r.tone === 'nested' ? { background: 'var(--beige-100)' } : null),
          ...cardStyle?.(r),
        };

        return onOpen && !r.inert ? (
          /* A div, not a <button>: a cell may carry its own button or <p>, and
             those are not valid inside a button. */
          <div
            key={r.id}
            role="button"
            tabIndex={0}
            className="card card-btn"
            style={style}
            onClick={(e) => {
              /* A control inside the card does its own job; it does not also open it. */
              const hit = (e.target as HTMLElement).closest('button,a,input,select,textarea');
              if (hit && e.currentTarget.contains(hit)) return;
              onOpen(r);
            }}
            onKeyDown={(e) => {
              if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                onOpen(r);
              }
            }}
          >
            {inner}
          </div>
        ) : (
          <div key={r.id} className="card" style={style}>
            {inner}
          </div>
        );
      })}
    </>
  );
}
